import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/shadcn/label';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Switch } from '@shared/components/ui/shadcn/switch';
import { TextArea } from '@shared/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import { DatePicker } from '@shared/components/ui/date-picker';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { format } from 'date-fns';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useCreateManualTransaction, useHoldedChartOfAccounts } from '../../hooks/useManualTransaction';
import { useWallet } from '../../hooks/useWallet';
import { HoldedChartAccountCombobox } from '../interaction';

export interface CreateManualTransactionModalRef {
  open: (walletId: number) => void;
  close: () => void;
}

interface CreateManualTransactionModalProps {
  onSuccess?: () => void;
}

const MAX_CONCEPT = 2000;

export const CreateManualTransactionModal = forwardRef<
  CreateManualTransactionModalRef,
  CreateManualTransactionModalProps
>(({ onSuccess }, ref) => {
  const { t } = useTranslation('walletsModule');
  const { toast } = useToast();
  const { isHoldedAccounting } = usePermissions();
  const showHoldedAccounting = isHoldedAccounting();
  const modalRef = useRef<ModalCenterRef>(null);
  const currency = useCurrency();

  const [walletId, setWalletId] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [concept, setConcept] = useState('');
  const [postToAccounting, setPostToAccounting] = useState(false);
  const [holdedAccountId, setHoldedAccountId] = useState('');
  const [accountingDate, setAccountingDate] = useState<Date | undefined>(undefined);

  const { data: walletDetail } = useWallet(walletId ?? undefined);

  const {
    data: chartAccounts = [],
    isLoading: chartLoading,
    isError: chartError,
    error: chartErrorData,
  } = useHoldedChartOfAccounts(walletId !== null && showHoldedAccounting && postToAccounting);

  const createManual = useCreateManualTransaction();

  const resetForm = () => {
    setAmount('');
    setConcept('');
    setPostToAccounting(false);
    setHoldedAccountId('');
    setAccountingDate(undefined);
  };

  useImperativeHandle(ref, () => ({
    open: (w) => {
      setWalletId(w);
      resetForm();
      modalRef.current?.openModal();
    },
    close: () => modalRef.current?.closeModal(),
  }));

  const handleClose = () => {
    resetForm();
    setWalletId(null);
  };

  const amountNum = Number(amount.replace(',', '.'));
  const conceptTrimmed = concept.trim();
  const hasValidAmount = amount.trim() !== '' && !Number.isNaN(amountNum) && amountNum !== 0;
  const hasValidConcept = conceptTrimmed.length > 0 && conceptTrimmed.length <= MAX_CONCEPT;

  const accountingOk =
    !postToAccounting ||
    (!chartLoading &&
      !chartError &&
      chartAccounts.length > 0 &&
      holdedAccountId.length > 0 &&
      holdedAccountId.length <= 64 &&
      accountingDate !== undefined);

  const isFormValid = walletId !== null && hasValidAmount && hasValidConcept && accountingOk;

  const handleSubmit = async () => {
    if (!walletId || !isFormValid) return;
    try {
      const payload = {
        wallet_id: walletId,
        amount: amountNum,
        concept: conceptTrimmed,
        post_to_accounting: postToAccounting,
        ...(postToAccounting && accountingDate
          ? {
              holded_counterpart_account_id: holdedAccountId,
              accounting_date: format(accountingDate, 'yyyy-MM-dd'),
            }
          : {}),
      };

      const created = await createManual.mutateAsync(payload);

      toast({
        title: t('manualTransaction.toast.successTitle'),
        description: t('manualTransaction.toast.successDescription', {
          balance: created.balance_total.toFixed(2),
          currency: walletDetail?.currency ?? currency,
        }),
      });
      resetForm();
      setWalletId(null);
      modalRef.current?.closeModal();
      onSuccess?.();
    } catch (error) {
      toast({
        title: t('manualTransaction.toast.errorTitle'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('manualTransaction.title')}
      type="default"
      width="lg"
      className="max-h-[90vh]"
      onCloseModal={handleClose}
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('manualTransaction.description')}</p>

        {walletId && walletDetail && (
          <p className="text-sm">
            <span className="text-muted-foreground">{t('manualTransaction.currentBalance')}:</span>{' '}
            <span className="font-medium tabular-nums">
              {formatCurrencyAmount(walletDetail.balance_total, walletDetail.currency)}
            </span>
          </p>
        )}

        {chartError && (
          <Alert variant="destructive" className="border-destructive/50">
            <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4" />
            <AlertTitle>{t('manualTransaction.accountsLoadError.title')}</AlertTitle>
            <AlertDescription>
              {getErrorMessage(chartErrorData) || t('manualTransaction.accountsLoadError.description')}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="manual-tx-amount" className="text-sm font-medium text-foreground">
            {t('manualTransaction.fields.amount')} *
          </Label>
          <Input
            id="manual-tx-amount"
            type="number"
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={createManual.isPending}
            placeholder={t('manualTransaction.placeholders.amount')}
            data-testid="manual-tx-amount"
          />
          <p className="text-xs text-muted-foreground">{t('manualTransaction.hints.amount')}</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="manual-tx-concept" className="text-sm font-medium text-foreground">
            {t('manualTransaction.fields.concept')} *
          </Label>
          <TextArea
            id="manual-tx-concept"
            value={concept}
            onChange={(e) => setConcept(e.target.value.slice(0, MAX_CONCEPT))}
            disabled={createManual.isPending}
            rows={4}
            placeholder={t('manualTransaction.placeholders.concept')}
            data-testid="manual-tx-concept"
          />
          <p className="text-xs text-muted-foreground text-right">
            {concept.length}/{MAX_CONCEPT}
          </p>
        </div>

        {showHoldedAccounting && (
          <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2">
            <div className="space-y-0.5">
              <Label htmlFor="manual-tx-post-accounting" className="text-sm font-medium text-foreground">
                {t('manualTransaction.fields.postToAccounting')}
              </Label>
              <p className="text-xs text-muted-foreground">{t('manualTransaction.hints.postToAccounting')}</p>
            </div>
            <Switch
              id="manual-tx-post-accounting"
              checked={postToAccounting}
              onCheckedChange={(v) => {
                setPostToAccounting(v);
                if (!v) {
                  setHoldedAccountId('');
                  setAccountingDate(undefined);
                } else {
                  setAccountingDate(new Date());
                }
              }}
              disabled={createManual.isPending}
              data-testid="manual-tx-post-accounting"
            />
          </div>
        )}

        {showHoldedAccounting && postToAccounting && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="manual-tx-accounting-date" className="text-sm font-medium text-foreground">
                {t('manualTransaction.fields.accountingDate')} *
              </Label>
              <DatePicker
                id="manual-tx-accounting-date"
                date={accountingDate}
                onDateChange={setAccountingDate}
                placeholder={t('manualTransaction.placeholders.accountingDate')}
                disabled={createManual.isPending}
                data-testid="manual-tx-accounting-date"
              />
              <p className="text-xs text-muted-foreground">{t('manualTransaction.hints.accountingDate')}</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-foreground">
                {t('manualTransaction.fields.holdedAccount')} *
              </Label>
              <HoldedChartAccountCombobox
                accounts={chartAccounts}
                value={holdedAccountId}
                storedValueField="num"
                onValueChange={setHoldedAccountId}
                disabled={createManual.isPending || chartAccounts.length === 0}
                loading={chartLoading}
                dataTestId="manual-tx-holded-account"
              />
              <p className="text-xs text-muted-foreground">{t('manualTransaction.holdedCombobox.help')}</p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={createManual.isPending}>
            {t('manualTransaction.buttons.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={!isFormValid || createManual.isPending}
            data-testid="manual-tx-submit"
          >
            {createManual.isPending ? t('manualTransaction.buttons.submitting') : t('manualTransaction.buttons.submit')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

CreateManualTransactionModal.displayName = 'CreateManualTransactionModal';
